import { FormattedMessage } from 'react-intl'

import { Currency } from '@/constants/currency'
import { formatAmount } from '@/lib/money'
import { getFullName, intersperse } from '@/lib/typography'
import { Checkbox, Typography } from '@/shared/ui'

import { Contractor, ContractorPaymentType } from '../../types'

type Props = {
  contractor: Contractor
  selectedContractors?: string[]
  onSelect?: (ids: string[]) => void
}

export const CSVContractorRow = ({
  onSelect,
  selectedContractors,
  contractor,
}: Props) => {
  const handleSelect = () => {
    if (!onSelect) return
    const ids = selectedContractors?.includes(contractor.id)
      ? selectedContractors.filter((id) => id !== contractor.id)
      : [...(selectedContractors ?? []), contractor.id]

    onSelect(ids)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className="border-neutral-gray-200 focus-visible:ring-neutral-gray-600 flex cursor-pointer items-center justify-between border-b transition-transform last:border-b-0 hover:scale-[1.01] focus-visible:ring-1 focus-visible:ring-offset-2 focus-visible:outline-hidden"
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === ' ') {
          handleSelect()
        }
      }}
    >
      <div className="flex items-center gap-4">
        {onSelect ? (
          <Checkbox
            tabIndex={-1}
            checked={selectedContractors?.includes(contractor.id)}
          />
        ) : null}

        <div className="flex flex-col py-3">
          <div className="flex items-center gap-1">
            <Typography bold>
              {getFullName(contractor) ?? <span className="inline-flex h-5" />}
            </Typography>
          </div>

          <CSVContractorDetailsRow contractor={contractor} />
        </div>
      </div>
    </div>
  )
}

export const CSVContractorDetailsRow = ({ contractor }: Props) => {
  return (
    <div className="text-neutral-gray-600 flex items-center gap-1">
      {(() => {
        switch (contractor.paymentType) {
          case ContractorPaymentType.PAY_AS_YOU_GO:
            return intersperse(
              [
                <Typography key="payAsYouGoRole">
                  {contractor.contractorRole}
                </Typography>,
                <Typography key="payAsYouGoPaymentType">
                  <FormattedMessage
                    id="contract.type.label"
                    defaultMessage="{state, select, FIXED_RATE {Fixed-rate} PAY_AS_YOU_GO {Pay-as-you-go} other {}}"
                    values={{ state: contractor.paymentType }}
                  />
                </Typography>,
              ],
              '•',
            )

          case ContractorPaymentType.FIXED_RATE:
            return intersperse(
              [
                <Typography key="payAsYouGoRole">
                  {contractor.contractorRole}
                </Typography>,
                <Typography key="payAsYouGoPaymentType">
                  <FormattedMessage
                    id="contract.type.label"
                    defaultMessage="{state, select, FIXED_RATE {Fixed-rate} PAY_AS_YOU_GO {Pay-as-you-go} other {}}"
                    values={{ state: contractor.paymentType }}
                  />
                </Typography>,
                <Typography key="fixedRatePaymentDetails">
                  {formatAmount({
                    amount: 1000,
                    currency: Currency.USDC,
                  })}
                </Typography>,
              ],
              '•',
            )

          default:
            return ''
        }
      })()}
    </div>
  )
}
